/**
 * FeedbackSupportPage Component
 *
 * Renders consent gate when consent is not granted, or the embedded
 * Featurebase portal when consent is granted. Manages embed lifecycle
 * including initialization, theme re-init, portal navigation, and
 * error recovery.
 *
 * Uses dispatch-success = ready pattern: when `initEmbedWidget()` returns
 * true, the embed is considered ready (matching feedback widget precedent
 * in FeaturebaseProvider). The callback only surfaces real errors.
 */

// 1. External dependencies
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ExternalLink, ShieldCheck } from 'lucide-react';

// 2. Workspace packages
import { Button, Spinner } from '@enfomentis/ui';

// 3. Internal absolute imports
import { useFeaturebase, featurebaseService, ConsentDialog } from '@/featurebase';
import { useTheme } from '@/hooks/useTheme';

/** Embed-local lifecycle status, separate from provider readiness. */
type EmbedStatus = 'idle' | 'initializing' | 'ready' | 'error';

interface FeedbackSupportPageProps {
  /** Portal path to navigate to (e.g. '/' for home). */
  portalPath: string;
  /** Incrementing nonce to force re-navigation even when path hasn't changed. */
  portalNonce: number;
}

export function FeedbackSupportPage({ portalPath, portalNonce }: FeedbackSupportPageProps) {
  const { consent, readiness, retryInitialization } = useFeaturebase();
  const { effectiveTheme } = useTheme();
  const [isConsentDialogOpen, setConsentDialogOpen] = useState(false);
  const [embedStatus, setEmbedStatus] = useState<EmbedStatus>('idle');

  // StrictMode double-init guard
  const initGuardRef = useRef(false);
  // Stale-callback generation counter: incremented on every tear-down path
  const initGenRef = useRef(0);

  // --- Embed initialization effect ---
  useEffect(() => {
    if (consent !== 'granted' || readiness !== 'ready' || embedStatus !== 'idle') {
      return;
    }

    if (initGuardRef.current) return;
    initGuardRef.current = true;

    const currentGen = ++initGenRef.current;
    setEmbedStatus('initializing');

    const success = featurebaseService.initEmbedWidget(
      {
        embedOptions: {
          path: portalPath,
        },
        stylingOptions: {
          theme: effectiveTheme,
          hideMenu: false,
          hideLogo: true,
        },
      },
      (err) => {
        // Dispatch-success = ready: callback only surfaces real errors.
        // Guard against stale callbacks from prior init attempts.
        if (err && currentGen === initGenRef.current) {
          setEmbedStatus('error');
        }
      },
    );

    if (success) {
      // Dispatch succeeded — treat as ready (unreliable widgetReady callback).
      setEmbedStatus('ready');
    } else {
      setEmbedStatus('error');
    }
  }, [consent, readiness, embedStatus, effectiveTheme, portalPath]);

  // --- Portal command effect ---
  // When portalPath or portalNonce change, tear down and re-init with new path.
  const prevPathRef = useRef(portalPath);
  const prevNonceRef = useRef(portalNonce);
  useEffect(() => {
    if (prevPathRef.current === portalPath && prevNonceRef.current === portalNonce) return;
    prevPathRef.current = portalPath;
    prevNonceRef.current = portalNonce;

    if (embedStatus !== 'ready' && embedStatus !== 'initializing') return;

    const container = document.querySelector('[data-featurebase-embed]');
    if (container) {
      container.innerHTML = '';
    }
    initGuardRef.current = false;
    ++initGenRef.current;
    setEmbedStatus('idle'); // triggers re-init via the init effect
  }, [portalPath, portalNonce, embedStatus]);

  // --- Theme re-init effect ---
  // When theme changes while embed is ready or initializing, tear down and re-init.
  const prevThemeRef = useRef(effectiveTheme);
  useEffect(() => {
    if (prevThemeRef.current === effectiveTheme) return;
    prevThemeRef.current = effectiveTheme;

    if (embedStatus !== 'ready' && embedStatus !== 'initializing') return;

    const container = document.querySelector('[data-featurebase-embed]');
    if (container) {
      container.innerHTML = '';
    }
    initGuardRef.current = false;
    ++initGenRef.current;
    setEmbedStatus('idle'); // triggers re-init via the init effect
  }, [effectiveTheme, embedStatus]);

  // --- Cleanup on unmount ---
  useEffect(() => {
    return () => {
      initGuardRef.current = false;
      initGenRef.current = -1;
    };
  }, []);

  const handleRetryEmbed = () => {
    const container = document.querySelector('[data-featurebase-embed]');
    if (container) {
      container.innerHTML = '';
    }
    initGuardRef.current = false;
    setEmbedStatus('idle');
  };

  // --- Render precedence (short-circuit) ---

  // 1. Consent gate
  if (consent !== 'granted') {
    return (
      <>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md text-center space-y-4">
            <div className="flex justify-center">
              <ShieldCheck className="w-12 h-12 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary">
              Enable Feedback &amp; Support
            </h3>
            <p className="text-sm text-text-secondary">
              Connect to our support team, submit feedback, view the product
              roadmap, and browse our help center — all within EnfoMentis.
            </p>
            <p className="text-xs text-text-secondary">
              Your <strong>name</strong> and <strong>email</strong> will be
              shared with Featurebase to set up your support channel. Your
              trading data is never sent.
            </p>
            <Button
              color="primary"
              variant="solid"
              onPress={() => setConsentDialogOpen(true)}
            >
              Get Started
            </Button>
          </div>
        </div>

        <ConsentDialog
          isOpen={isConsentDialogOpen}
          onOpenChange={setConsentDialogOpen}
        />
      </>
    );
  }

  // 2. Provider loading
  if (readiness === 'loading' || readiness === 'unloaded') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-4">
        <Spinner size="lg" />
        <p className="text-sm text-text-secondary">
          Loading support portal&hellip;
        </p>
      </div>
    );
  }

  // 3. Provider error
  if (readiness === 'error') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-4">
        <AlertTriangle className="w-10 h-10 text-danger" />
        <p className="text-sm text-text-secondary text-center max-w-sm">
          The support portal couldn&apos;t be loaded. Please try again or visit
          the portal directly.
        </p>
        <div className="flex items-center gap-3">
          <Button
            variant="flat"
            color="primary"
            size="sm"
            onPress={retryInitialization}
          >
            Retry
          </Button>
          <a
            href="https://support.enfomentis.app"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            Open in new tab
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    );
  }

  // 4. Embed error (provider is ready but embed init failed)
  if (embedStatus === 'error') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-4">
        <AlertTriangle className="w-10 h-10 text-danger" />
        <p className="text-sm text-text-secondary text-center max-w-sm">
          The embedded portal failed to initialize. Please try again or visit
          the portal directly.
        </p>
        <div className="flex items-center gap-3">
          <Button
            variant="flat"
            color="primary"
            size="sm"
            onPress={handleRetryEmbed}
          >
            Retry
          </Button>
          <a
            href="https://support.enfomentis.app"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            Open in new tab
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    );
  }

  // 5. Portal container (readiness === 'ready')
  return (
    <div className="flex-1 min-h-0 relative">
      <div data-featurebase-embed className="h-full" />
    </div>
  );
}
